import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { ProductCategory } from '../../types';
import { productService, CreateCategoryRequest } from '../../services/productService';
import { chartOfAccountsService, GLAccountDTO } from '../../services/accountingService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './ProductCategoryCreatePage.css';
import { ArrowLeft, Save } from 'lucide-react';

const ACCOUNT_MAPPING_FIELDS = ['defaultInventoryAccountId', 'defaultCOGSAccountId', 'defaultRevenueAccountId'];

const ProductCategoryCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]);
  const [glAccounts, setGlAccounts] = useState<GLAccountDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateCategoryRequest>({
    name: '',
    description: '',
    parentCategoryId: undefined,
    isActive: true,
    defaultInventoryAccountId: null,
    defaultCOGSAccountId: null,
    defaultRevenueAccountId: null,
  });

  useEffect(() => {
    loadParentCategories();
    // Optional — the Accounting app may not be enabled for this company.
    chartOfAccountsService.getAll(false)
      .then((res) => {
        const data = (res?.data as any)?.data ?? res?.data ?? [];
        if (Array.isArray(data)) setGlAccounts(data);
      })
      .catch(() => setGlAccounts([]));
  }, []);

  const loadParentCategories = async () => {
    try {
      const response = await productService.getCategories({ pageSize: 100 });
      if (response.data.data) {
        setAllCategories(response.data.data as ProductCategory[]);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'parentCategoryId'
          ? (value ? parseInt(value) : undefined)
          : ACCOUNT_MAPPING_FIELDS.includes(name)
          ? (value ? parseInt(value) : null)
          : value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError(t('productCategory.formValidation.categoryNameRequired'));
      return false;
    }
    if (formData.name.length > 30) {
      setError(t('productCategory.formValidation.categoryNameMaxLength'));
      return false;
    }
    if (formData.description && formData.description.length > 200) {
      setError(t('productCategory.formValidation.descriptionMaxLength'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await productService.createCategory(formData);
      navigate('/products/categories', {
        state: { message: t('productCategory.categoryCreatedSuccess') },
      });
    } catch (err: unknown) {
      console.error('Category creation error:', err);
      setError(parseApiError(err, t('productCategory.failedToCreateCategory')).message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/categories')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('productCategory.createTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/categories')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="create-content">
          {/* Form Card */}
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('productCategory.categoryDetails')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      {t('productCategory.categoryName')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/30</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={30}
                      placeholder={t('productCategory.categoryName')}
                      required
                    />
                    <small className="form-helper">
                      {t('productCategory.descriptionHelperText')}
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('productCategory.description')}
                      <span className="char-counter">{(formData.description || '').length}/200</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      maxLength={200}
                      placeholder={t('productCategory.description')}
                      rows={4}
                    />
                    <small className="form-helper">
                      {t('productCategory.descriptionHelperText')}
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('productCategory.parentCategoryOptional')}</label>
                    <select
                      className="form-select"
                      name="parentCategoryId"
                      value={formData.parentCategoryId || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('productCategory.noParentRootCategory')}</option>
                      {allCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <small className="form-helper">
                      {t('productCategory.parentCategoryHelperText')}
                    </small>
                  </div>

                  {/* ── Active status ── */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                      />
                      <label
                        htmlFor="isActive"
                        className="form-label"
                        style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}
                      >
                        {t('productCategory.active', 'Active')}
                      </label>
                    </div>
                    <small className="form-helper">
                      {t(
                        'productCategory.activeHelperText',
                        'Inactive categories will not be available for product assignment.',
                      )}
                    </small>
                  </div>

                  {glAccounts.length > 0 && (
                    <>
                      <div className="form-group" style={{ marginTop: 8, marginBottom: 4 }}>
                        <label className="form-label" style={{ fontWeight: 700 }}>Accounting (Optional)</label>
                        <small className="form-helper">
                          Override which GL accounts this category's sales, cost of goods, and inventory movements post to.
                          Leave blank to use the company's default accounts.
                        </small>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Revenue Account</label>
                        <select
                          className="form-select"
                          name="defaultRevenueAccountId"
                          value={formData.defaultRevenueAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Revenue').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Cost of Goods Sold Account</label>
                        <select
                          className="form-select"
                          name="defaultCOGSAccountId"
                          value={formData.defaultCOGSAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Expense').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Inventory Account</label>
                        <select
                          className="form-select"
                          name="defaultInventoryAccountId"
                          value={formData.defaultInventoryAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Asset').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="create-sidebar">
            {/* Category Hierarchy Card */}
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('productCategory.categoryHierarchy')}</h3>
              </div>
              <div className="create-card-body">
                <div className="create-help-section">
                  <p className="create-help-text">
                    {t('productCategory.categoriesCanBeOrganized')}
                  </p>
                  <ul className="create-help-list">
                    <li>
                      <strong>{t('productCategory.rootCategories')}:</strong> {t('productCategory.rootCategoriesDesc')}
                    </li>
                    <li>
                      <strong>{t('productCategory.subCategory')}:</strong> {t('productCategory.subCategoriesDesc')}
                    </li>
                  </ul>
                  <hr className="create-divider" />
                  <p className="create-help-text">{t('productCategory.exampleStructure')}</p>
                  <ul className="create-help-list">
                    <li>
                      Electronics ({t('productCategory.rootCategory')})
                      <ul>
                        <li>Computers ({t('productCategory.subCategory')})</li>
                        <li>Mobile Devices ({t('productCategory.subCategory')})</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Best Practices Card */}
            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('productCategory.bestPractices')}</h3>
              </div>
              <div className="create-card-body">
                <div className="create-help-section">
                  <ul className="create-help-list">
                    <li>{t('productCategory.usesClear')}</li>
                    <li>{t('productCategory.keepHierarchy')}</li>
                    <li>{t('productCategory.avoidSpecial')}</li>
                    <li>{t('productCategory.useConsistent')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductCategoryCreatePage;
